/**
 * A UUIDv7 (RFC 9562): 48 bits of Unix milliseconds, then random bits.
 *
 * The session and set ids are minted HERE, on the device, because the id is
 * what makes an outbox replay idempotent (plan D19): the server answers a
 * second POST of the same id with the stored row. v7 rather than v4 so the
 * ids sort in the order they were made, which is the order the server's
 * `text PRIMARY KEY` indexes them in.
 *
 * `uuid` is `@repo/client`'s dependency, not the app's, and a dependency
 * cannot be added from this task; twenty lines of bit layout are cheaper
 * than the question. Time is a parameter (a pure helper never reads the
 * clock); randomness defaults to `crypto.getRandomValues`, which WebView and
 * Node both have.
 */

type Fill = (bytes: Uint8Array<ArrayBuffer>) => void;

const defaultFill: Fill = (bytes) => {
  crypto.getRandomValues(bytes);
};

const HEX = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, "0")
);

export function uuidv7(nowMs: number, fill: Fill = defaultFill): string {
  const bytes = new Uint8Array(16);
  fill(bytes);
  // 48-bit big-endian timestamp. Division, not shifts: `>>` truncates to 32.
  let ms = Math.max(0, Math.floor(nowMs));
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  // Arithmetic, not bitwise: the high nibble becomes 7 (the version) and the
  // top two bits of byte 8 become 10 (the variant).
  bytes[6] = 0x70 + ((bytes[6] ?? 0) % 16);
  bytes[8] = 0x80 + ((bytes[8] ?? 0) % 64);
  const hex = Array.from(bytes, (b) => HEX[b]).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
